import * as React from 'react';
import { Checkbox, Input } from '../Input';
import { Button } from '../Button';
import { Validated } from '../Validated';
import {
  FieldType,
  IDrawingObject,
  getFieldRowClassName,
  getReferencedFieldRemoveTitle,
} from './DrawingObjectsInput';
import { TableScroll } from '../TableScroll';

export interface IOutputField {
  readonly id?: number;
  name: string;
  formula: string;
  unit: string;
  export: boolean;
}

function defaultOutputField(): IOutputField {
  return {
    name: '',
    formula: '',
    unit: '',
    export: false,
  };
}

interface OutputFieldsInputProps {
  value: IOutputField[];
  drawingObjects: IDrawingObject[];
  validation: string | undefined;
  /** 入力チェックで誤りが見つかった行の位置。該当する行を強調する */
  invalidRowIndexes: number[];
  onChange: (fields: IOutputField[]) => void;
  onDrawingObjectsChange: (objs: IDrawingObject[]) => void;
}

export const OutputFieldsInput: React.FC<OutputFieldsInputProps> = function (
  props
) {
  function changeValue(index: number, field: IOutputField) {
    const next = props.value.slice();
    next[index] = field;
    props.onChange(next);
  }
  function addValue() {
    props.onChange([...props.value, defaultOutputField()]);
  }
  /** index番目の計算項目が、作画文字列のどれかに参照されているか */
  function isReferenced(index: number): boolean {
    return props.drawingObjects.some((obj) => {
      if (obj.fieldType !== FieldType.OutputField) {
        return false;
      }

      return obj.fieldIndex === index;
    });
  }
  function removeValue(index: number) {
    if (isReferenced(index)) {
      return;
    }

    // 削除した行より後ろを参照している作画文字列の参照位置を1つ詰める
    const shiftedDrawingObjects = props.drawingObjects.map((obj) => {
      if (obj.fieldType !== FieldType.OutputField) {
        return obj;
      }

      if (obj.fieldIndex <= index) {
        return obj;
      }

      return { ...obj, fieldIndex: obj.fieldIndex - 1 };
    });

    props.onDrawingObjectsChange(shiftedDrawingObjects);
    props.onChange(props.value.filter((_, i) => i !== index));
  }

  function row(field: IOutputField, index: number) {
    const referenced = isReferenced(index);

    return (
      <tr
        key={index}
        className={getFieldRowClassName(props.invalidRowIndexes.indexOf(index) !== -1)}
      >
        <td>
          <Input
            value={field.name}
            onChange={(event) =>
              changeValue(index, { ...field, name: event.target.value })
            }
          />
        </td>
        <td>
          <Input
            value={field.formula}
            onChange={(event) =>
              changeValue(index, { ...field, formula: event.target.value })
            }
          />
        </td>
        <td>
          <Checkbox
            value={field.export}
            onChange={(value) =>
              changeValue(index, { ...field, export: value })
            }
          />
        </td>
        <td>
          <Input
            value={field.unit}
            onChange={(event) =>
              changeValue(index, { ...field, unit: event.target.value })
            }
          />
        </td>
        <td className="App-table_button_cell">
          <Button
            type="button"
            mini={true}
            color="red"
            disabled={referenced}
            title={getReferencedFieldRemoveTitle(referenced)}
            onClick={() => removeValue(index)}
          >
            削除
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <div>
      <Validated message={props.validation}>
        <TableScroll storageKey="codeOutputFields">
          <table className="App-table">
            <thead>
              <tr>
                <th>
                  名称
                  <span className="App-label_required" aria-hidden="true">*</span>
                </th>
                <th>
                  計算式
                  <span className="App-label_required" aria-hidden="true">*</span>
                </th>
                <th>集計表に出力</th>
                <th>単位</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>{props.value.map(row)}</tbody>
          </table>
        </TableScroll>
      </Validated>
      <div className="PlaceCreateCodePage-table_add_row">
        <Button type="button" mini={true} color="white" onClick={() => addValue()}>
          行を追加
        </Button>
      </div>
    </div>
  );
};
